// Owns ONNX Runtime Web sessions for the plugin system. One session per
// model URL. Plugins call `ortBridge.load(url)` once during init and keep
// the returned sessionId for later `ortBridge.run(sessionId, input)` calls.
//
// Design notes:
//   - We use onnxruntime-web's *bundled* distribution (the default
//     `import 'onnxruntime-web'` entrypoint, ort.bundle.min.mjs). The
//     bundle annotates its single dynamic import with `/*@vite-ignore*/`,
//     which tells Vite not to rewrite it — Vite serves the runtime files
//     from /onnxruntime/ raw, populated by aosTools/threejs/copy_onnxruntime_assets.mjs.
//   - One Tensor instance per session is reused across frames; callers
//     pass a Float32Array of the model's expected input length.
//   - run() is async (await session.run). Plugins that call run() from a
//     per-frame tick should await it.
//
// EP + IO binding:
//   - ep: 'wasm' (default) or 'webgpu'.
//   - On 'webgpu', `iobinding: true` runs the model the way the native server
//     does (run_with_iobinding): a PERSISTENT gpu-buffer input (write the small
//     control vector each call) + `preferredOutputLocation: 'gpu-buffer'` so the
//     output stays on-device, plus `enableGraphCapture` so ORT records the
//     command list ONCE and replays it — no per-call JS kernel-dispatch loop.
//     The output is read back with getData() only because the plugin consumers
//     here need CPU data (e.g. a CPU vertex blend). Naive webgpu (no
//     iobinding) uploads the input + reads back the output AND re-dispatches
//     every kernel from JS each call — pathologically slow for small MLPs.

import * as ort from 'onnxruntime-web'

ort.env.wasm.wasmPaths = '/onnxruntime/'
// Prefer the discrete GPU for ORT's WebGPU EP. Without this, ORT requests a
// default (low-power) adapter → the integrated GPU on a dual-GPU system, where
// inference runs several times slower. (When the texavatars path sets
// ort.env.webgpu.adapter, that shared high-performance adapter wins instead.)
if (ort.env?.webgpu) ort.env.webgpu.powerPreference = 'high-performance'

class OrtBridge {
  constructor() {
    this._sessions = new Map()
    this._nextId = 1
  }

  async load(url, { inputLen, ep = 'wasm', iobinding = false } = {}) {
    const wantGpu = ep === 'webgpu'
    const tryEps = wantGpu ? ['webgpu', 'wasm'] : ['wasm']
    // Build the WHOLE session (create + resident gpu-buffer setup + a graph-
    // capture warmup) inside the fallback loop, so a failure at ANY of those
    // steps — not just create — falls through to the next EP. The warmup matters
    // most: with enableGraphCapture ORT records the command list on the FIRST
    // run, so an "unsupported op under capture" only surfaces there; running it
    // here surfaces it during load() where we can still drop to wasm, instead of
    // deep in the plugin's first real run (which had no fallback).
    let built = null
    for (const e of tryEps) {
      let session = null, inputBuffer = null
      try {
        const resident = (e === 'webgpu' && iobinding)
        const extra = {}
        if (resident) {
          extra.preferredOutputLocation = 'gpu-buffer'
          // ORT-Web bug: with kernel profiling active, the async timestamp-query
          // resolve does `this.kernels.get(id).kernelType` on kernels a graph-
          // capture replay no longer has registered → internal TypeError
          // "reading 'kernelType'" (ort.bundle WebGpuBackend). Capture also
          // hides kernels from the profiler, so it's useless on profiling runs.
          // The profiler installs AFTER this plugin loads, so check the same
          // signal it keys on (?perf=1) in addition to the live mode.
          const profilingOn = ort.env.webgpu?.profiling?.mode === 'default'
            || (typeof location !== 'undefined' && /[?&]perf=1/.test(location.search))
          if (!profilingOn) extra.enableGraphCapture = true
          else console.info(`[ortBridge] kernel profiling active — graph capture disabled for ${url}`)
        }
        session = await ort.InferenceSession.create(url, {
          executionProviders: [e], graphOptimizationLevel: 'all', ...extra,
        })
        const inputName = session.inputNames[0]
        const outputName = session.outputNames[0]
        let inputTensor = null, tensor = null
        if (resident && inputLen) {
          // Persistent gpu-buffer input + a fromGpuBuffer tensor reused every run
          // (graph capture forbids swapping the external input buffer).
          const device = ort.env.webgpu.device
          inputBuffer = device.createBuffer({
            size: Math.max(16, inputLen * 4),
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
          })
          inputTensor = ort.Tensor.fromGpuBuffer(inputBuffer, { dataType: 'float32', dims: [1, inputLen] })
          if (extra.enableGraphCapture) {
            // Warmup run records the capture now (input is the zero-init buffer,
            // output discarded). getData drains it without releasing ORT's reused
            // output buffer (see _runResident).
            const warm = await session.run({ [inputName]: inputTensor })
            await warm[outputName].getData()
          }
        } else if (inputLen) {
          tensor = new ort.Tensor('float32', new Float32Array(inputLen), [1, inputLen])
        }
        built = { session, actualEp: e, resident, inputName, outputName, inputBuffer, inputTensor, tensor }
        break
      } catch (err) {
        console.warn(`[ortBridge] EP '${e}'${iobinding ? '+iobinding' : ''} failed for ${url}: ${String(err).slice(0, 160)}`)
        try { inputBuffer?.destroy?.() } catch { /* ignore */ }
        try { session?.release?.() } catch { /* ignore */ }
      }
    }
    if (!built) throw new Error(`[ortBridge] all EPs failed for ${url}`)
    const id = this._nextId++
    const { session, actualEp, resident, inputName, outputName, inputBuffer, inputTensor, tensor } = built
    this._sessions.set(id, { session, inputName, outputName, inputLen, ep: actualEp, resident, inputBuffer, inputTensor, tensor })
    console.info(`[ortBridge] loaded ${url} → session #${id} on '${actualEp}'${resident ? ' (iobinding: gpu-buffer io + graph capture)' : ''} (in "${inputName}", out "${outputName}")`)
    return id
  }

  // Pass a Float32Array of length inputLen. Returns the Float32Array of the
  // first output — DO NOT retain across calls (the buffer is reused).
  async run(sessionId, input) {
    const entry = this._sessions.get(sessionId)
    if (!entry) throw new Error(`[ortBridge] no such session #${sessionId}`)
    if (entry.resident) return this._runResident(entry, input)
    let tensor = entry.tensor
    if (!tensor || (entry.inputLen && tensor.data.length !== entry.inputLen)) {
      tensor = new ort.Tensor('float32', input, [1, input.length])
    } else {
      tensor.data.set(input)
    }
    const result = await entry.session.run({ [entry.inputName]: tensor })
    return result[entry.outputName].data
  }

  async _runResident(entry, input) {
    // The persistent input buffer is a fixed size (graph capture forbids
    // swapping it), so a wrong-length input would leave stale tail bytes or
    // overflow the buffer. Reject loudly rather than run on corrupt input —
    // the non-resident run() guards length too.
    if (entry.inputLen && input.length !== entry.inputLen) {
      throw new Error(`[ortBridge] resident session expects ${entry.inputLen} inputs, got ${input.length}`)
    }
    const device = ort.env.webgpu.device
    device.queue.writeBuffer(entry.inputBuffer, 0, input.buffer, input.byteOffset, input.byteLength)
    const result = await entry.session.run({ [entry.inputName]: entry.inputTensor })
    const outT = result[entry.outputName]
    const data = await outT.getData() // gpu → cpu (consumers here need CPU data)
    // Graph capture reuses ORT's output buffer across runs — don't dispose it.
    return data
  }

  // Free a session. Called by pluginLoader during teardown (page unload).
  release(sessionId) {
    const entry = this._sessions.get(sessionId)
    if (!entry) return
    entry.session.release?.()
    try { entry.inputBuffer?.destroy?.() } catch { /* ignore */ }
    this._sessions.delete(sessionId)
  }
}

export const ortBridge = new OrtBridge()

if (typeof window !== 'undefined') {
  window.__ORT_BRIDGE__ = ortBridge
}
